import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtMonto } from "../api/client";
import { Icon } from "../components/Icon";

const COLS = "118px minmax(0,1.2fr) 112px 116px minmax(0,1fr) 132px 70px";

export default function HistoricoPage() {
  const [items, setItems] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [trayendo, setTrayendo] = useState(false);
  const [decision, setDecision] = useState("");
  const [falla, setFalla] = useState<string | null>(null);
  // Punto de continuación. `null` = no hay más que traer.
  const [siguiente, setSiguiente] = useState<number | null>(null);
  const [quien, setQuien] = useState("");
  const [texto, setTexto] = useState("");
  const [dias, setDias] = useState(30);
  // Sin el catálogo la columna de motivo mostraría el hash de Admin.
  const [motivos, setMotivos] = useState<Record<string, string>>({});

  useEffect(() => {
    api.getRejectReasons()
      .then((r) => setMotivos(Object.fromEntries(
        (r.items ?? []).map((x) => [x.id, x.label]))))
      .catch(() => setMotivos({}));
  }, []);

  const desde = useRef(Math.floor(Date.now() / 1000) - 30 * 24 * 3600);
  desde.current = Math.floor(Date.now() / 1000) - dias * 24 * 3600;

  // El histórico se trae de a tramos, no entero: a ~2800 depósitos por mes,
  // "todo" son megabytes que el navegador tiene que recibir, parsear y pintar
  // para mostrar las primeras veinte filas.
  const traer = useCallback(async (hasta: number, acumular: boolean) => {
    if (acumular) setTrayendo(true); else setCargando(true);
    try {
      const filtros: Record<string, string> = decision ? { decision } : {};
      const r = await api.getHistorico(desde.current, hasta, filtros);
      setItems((previo) => (acumular ? [...previo, ...(r.items ?? [])] : (r.items ?? [])));
      setSiguiente(r.hay_mas ? r.siguiente_hasta : null);
      setFalla(null);
    } catch (e: any) {
      // Al acumular no se borra lo que ya se está mostrando: una falla
      // paginando no puede hacer desaparecer lo que el analista ya tenía.
      if (!acumular) setItems([]);
      setFalla(e?.message || "No se pudo cargar el histórico.");
    } finally {
      setCargando(false); setTrayendo(false);
    }
  }, [decision, dias]);

  useEffect(() => {
    setSiguiente(null);
    traer(Math.floor(Date.now() / 1000), false);
  }, [traer]);

  function exportarCSV() {
    const cab = "fecha,titular,monto,moneda,decision,operador,certeza,simulado";
    const filas = items.map((i) => [
      new Date((i.ts ?? 0) * 1000).toISOString(),
      i.titular, i.monto, i.moneda, i.decision, i.operador, i.certeza, i.simulated,
    ].join(","));
    const url = URL.createObjectURL(
      new Blob([[cab, ...filas].join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `historico-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const aprobados = items.filter((i) => i.decision === "approved").length;
  const rechazados = items.filter((i) => i.decision === "rejected").length;
  const abiertos = items.length - aprobados - rechazados;
  const porAgente = items.filter((i) => i.operador === "auto").length;
  const personas = [...new Set(items.map((i) => i.operador).filter(
    (o) => o && o !== "auto"))].sort();

  const aguja = texto.trim().toLowerCase();
  const visibles = items.filter((i) => {
    if (quien && (quien === "auto" ? i.operador !== "auto" : i.operador !== quien))
      return false;
    if (aguja) {
      const heno = `${i.titular ?? ""} ${i.execution_id ?? ""} ${i.operador ?? ""}`;
      if (!heno.toLowerCase().includes(aguja)) return false;
    }
    return true;
  });

  return (
    <>
      {/* Encabezado derivado de los datos, no cuatro KPI sueltos: la primera
          línea ya dice qué se está mirando. */}
      <div className="card card-pad triaje" style={{ marginBottom: 14 }}>
        <div className="triaje-top">
          <div>
            <div className="triaje-titulo">
              {items.length} decisiones registradas
            </div>
            <div className="triaje-periodo">
              {dias === 1 ? "hoy" : `últimos ${dias} días`} · {porAgente} las resolvió el agente
            </div>
          </div>
          <div className="triaje-resumen tabular">
            {aprobados} aprobados · {rechazados} rechazados
            {abiertos > 0 && <> · {abiertos} sin resolver</>}
          </div>
        </div>

        {items.length > 0 && (
          <div className="triaje-barra" aria-hidden="true">
            <span style={{ width: `${100 * aprobados / items.length}%`, background: "var(--accent)" }} />
            <span style={{ width: `${100 * rechazados / items.length}%`, background: "var(--danger)" }} />
            <span style={{ width: `${100 * abiertos / items.length}%`, background: "var(--warn)" }} />
          </div>
        )}

        {abiertos > 0 && (
          <div className="met-aviso met-aviso-warn">
            <b>{abiertos} quedaron sin resolver.</b> Entraron al sistema pero
            nadie registró una decisión sobre ellos.
          </div>
        )}
      </div>

      <div className="filters">
        <div className="buscador">
          <Icon name="search" />
          <input value={texto} placeholder="Titular, analista o id de ejecución"
                 onChange={(e) => setTexto(e.target.value)} />
          {texto && (
            <button className="buscador-x" onClick={() => setTexto("")}
                    aria-label="Limpiar búsqueda">✕</button>
          )}
        </div>

        <select value={dias} onChange={(e) => setDias(Number(e.target.value))}>
          <option value={1}>Hoy</option>
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={90}>90 días</option>
        </select>

        {[["", "Todas"], ["approved", "Aprobados"], ["rejected", "Rechazados"]].map(([v, l]) => (
          <button key={v} className={"filter" + (decision === v ? " on" : "")}
                  onClick={() => setDecision(v)}>
            {l}
          </button>
        ))}

        {/* Quién decidió. Sin esto, para saber si algo lo resolvió el agente
            o una persona había que abrir cada fila. */}
        <select value={quien} onChange={(e) => setQuien(e.target.value)}>
          <option value="">Cualquiera</option>
          <option value="auto">El agente ({porAgente})</option>
          {personas.map((p) => (
            <option key={p} value={p}>
              {p} ({items.filter((i) => i.operador === p).length})
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={exportarCSV} disabled={!items.length}>
          Exportar CSV
        </button>
      </div>

      <div className="card">
        <div className="thead" style={{ gridTemplateColumns: COLS }}>
          <div>Fecha</div><div>Titular</div><div>Monto</div>
          <div>Decisión</div><div>Motivo</div><div>Quién decidió</div><div>Certeza</div>
        </div>
        {cargando && <div className="empty">Cargando...</div>}
        {!cargando && falla && (
          <div className="empty falla">
            <div className="falla-titulo">No se pudo cargar el histórico.</div>
            <div className="falla-detalle mono">{falla}</div>
            <button className="btn"
                    onClick={() => traer(Math.floor(Date.now() / 1000), false)}>
              Reintentar
            </button>
          </div>
        )}
        {!cargando && !falla && items.length === 0 && (
          <div className="empty">Sin registros en los últimos 30 días.</div>
        )}
        {visibles.map((i, n) => {
          const certeza = Math.round((i.certeza ?? 0) * 100);
          return (
            <div key={i.execution_id ?? n} className="trow" style={{ gridTemplateColumns: COLS }}>
              <div className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                {new Date((i.ts ?? 0) * 1000).toLocaleString("es-PE",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {i.titular ?? "—"}
              </div>
              <div className="tabular">{fmtMonto(i.monto, i.moneda)}</div>
              <div>
                <span className={"badge " + (i.decision === "approved" ? "ok"
                  : i.decision === "rejected" ? "bad" : "warn")}>
                  <span className="dot" />
                  {i.decision === "approved" ? "Aprobado"
                    : i.decision === "rejected" ? "Rechazado" : "Pendiente"}
                </span>
              </div>
              {/* Por qué se rechazó. Antes había que abrir el Admin para
                  saberlo, que es justo lo que este portal viene a evitar. */}
              <div className="col-falta" style={{ color: "var(--muted)" }}>
                {i.decision === "rejected"
                  ? (i.reasons ?? []).map((r: string) => motivos[r] ?? r).join(" · ") || "sin motivo registrado"
                  : i.decision === "approved" ? "—"
                  : "sin decidir"}
              </div>

              <div className="hist-quien">
                <span className="hist-avatar"
                      style={{ background: i.operador === "auto" ? "var(--raised)" : "var(--accent-soft)",
                               color: i.operador === "auto" ? "var(--muted)" : "var(--accent)" }}>
                  {i.operador === "auto" ? "IA" : (i.operador ?? "—").slice(0, 1).toUpperCase()}
                </span>
                <span className="hist-quien-txt">
                  {i.operador === "auto" ? "el agente" : i.operador ?? "—"}
                </span>
              </div>
              <div className="tabular" style={{
                color: certeza >= 85 ? "var(--text)"
                  : certeza >= 70 ? "var(--warn)" : "var(--danger)",
              }}>
                {certeza}%
              </div>
            </div>
          );
        })}

        {/* Traer más. Se pide explícitamente en vez de por scroll: el
            histórico se consulta con una pregunta en mente —"¿qué pasó el
            martes?"— y cargar solo mientras alguien busca es tirar datos. */}
        {siguiente !== null && !cargando && (
          <div className="empty" style={{ padding: "16px" }}>
            <button className="btn" disabled={trayendo}
                    onClick={() => traer(siguiente, true)}>
              {trayendo ? "Trayendo..." : `Traer más (${visibles.length} hasta ahora)`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
